<template>
  <div class="legal-page">
    <header class="legal-topbar">
      <router-link to="/" class="legal-back-link">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ $t('legal.backToApp_23fdde0') }}
      </router-link>
      <span class="legal-topbar-brand">Sparrow</span>
    </header>

    <div class="legal-shell">
      <aside class="legal-toc-desktop" aria-label="Table of contents">
        <p class="legal-toc-heading">{{ $t('legal.onThisPage_993081e') }}</p>
        <ul>
          <li v-for="s in sections" :key="s.id">
            <a :href="pageHashBase + '#' + s.id" @click="scrollTo(s.id, $event)">{{ s.label }}</a>
          </li>
        </ul>
      </aside>

      <main class="legal-content">
        <details class="legal-toc-mobile">
          <summary>{{ $t('legal.onThisPage_993081e') }}</summary>
          <ul>
            <li v-for="s in sections" :key="'m-' + s.id">
              <a :href="pageHashBase + '#' + s.id" @click="scrollTo(s.id, $event)">{{ s.label }}</a>
            </li>
          </ul>
        </details>

        <div class="legal-hero">
          <p class="legal-eyebrow">Sparrow</p>
          <h1>{{ $t('pages.TermsOfUse.title_b345ab3') }}</h1>
          <p class="legal-subtitle">{{ $t('pages.TermsOfUse.subtitle_205f278') }}</p>
          <div class="legal-meta">
            <span><strong>Version 1.0</strong> &middot; Effective 24 July 2026</span>
            <span>Applies to: sparrownests.com, app.sparrownests.com and related Sparrow services.</span>
          </div>
        </div>

        <p>By creating an account, accessing restricted reviews or submitting content, you agree to these Terms of Use and the Privacy Policy. If you do not agree, do not use the Platform.</p>

        <section id="section-1">
          <h2>1. Agreement and Platform Operator</h2>
          <p>These Terms of Use (&ldquo;Terms&rdquo;) form an agreement between you and Sparrow (&ldquo;Sparrow&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;) concerning your use of sparrownests.com, app.sparrownests.com and related services (the &ldquo;Platform&rdquo;).</p>
          <p>Sparrow may provide additional rules or notices for particular features. Those rules form part of these Terms where stated.</p>
        </section>

        <section id="section-2">
          <h2>2. Eligibility</h2>
          <p>You must be legally capable of agreeing to these Terms. The Platform is intended primarily for university students aged 18 or older. A younger university student may use the Platform only where permitted by law and with parent or guardian consent where required.</p>
        </section>

        <section id="section-3">
          <h2>3. Student Accounts and Verification</h2>
          <p>Certain content is restricted to verified student accounts. To qualify, you must have authorised access to an eligible university email address and provide accurate registration information.</p>
          <p>University-email verification confirms access to an eligible university email account. It does not prove that you lived in a particular property or room. Sparrow will use &ldquo;Verified stay&rdquo; only where it has separately checked appropriate occupancy evidence.</p>
          <p>You must keep your login credentials confidential and notify Sparrow promptly of suspected misuse. You are responsible for activity through your account to the extent permitted by law.</p>
        </section>

        <section id="section-4">
          <h2>4. Restricted Review Access and Account Sharing</h2>
          <p>Student accounts are personal, limited, non-transferable and intended only for the verified student&rsquo;s own use. You must not share, sell, lend, transfer or allow another person to use your account.</p>
          <p>You must not access Sparrow on behalf of, at the direction of or for the benefit of a landlord, operator, university housing office, agency, property manager or other housing provider. Housing providers must use the channels made available to them by Sparrow.</p>
          <p>You must not screenshot, scrape, download, reproduce, republish, forward or distribute restricted reviews or photographs outside the Platform except with Sparrow&rsquo;s written permission or where required by law. Sparrow may use access logs, rate limits, session controls, watermarks and other measures to detect and deter misuse.</p>
        </section>

        <section id="section-5">
          <h2>5. Nature of the Service</h2>
          <p>Sparrow is an independent student housing experience and information platform. Unless expressly stated otherwise, Sparrow is not a landlord, letting agent, property manager, university housing office, booking platform, safety inspector, surveyor or guarantor.</p>
          <p>Property conditions, prices, availability, management arrangements and surrounding conditions can change. Users must independently verify information before making housing, financial, legal or safety decisions.</p>
        </section>

        <section id="section-6">
          <h2>6. Property Pages, Maps and Verification Status</h2>
          <p>Sparrow may create separate pages for a building, floor or exact room and may display an exact building address or map pin. This allows students to distinguish experiences between rooms at the same property.</p>
          <p>Sparrow may display genuine university, company, operator or agency names where Sparrow reasonably believes the organisation is connected with the accommodation. Individual private landlords and individual agents are normally described by category rather than by personal name.</p>
          <p>The labels &ldquo;Property details verified&rdquo; and &ldquo;Property details unverified&rdquo; relate only to Sparrow&rsquo;s checking of selected basic information. A verified label does not represent a physical inspection, safety certification, guarantee, recommendation or endorsement.</p>
        </section>

        <section id="section-7">
          <h2>7. Submitting Reviews</h2>
          <p>You may submit a review only about accommodation you personally occupied or directly experienced. Your review must be honest, accurate to the best of your knowledge and based on first-hand experience.</p>
          <p>Reviews should describe observable facts and genuine opinions. You must not present speculation as fact, make knowingly false statements, impersonate another person or submit content primarily to threaten, harass, retaliate against or unfairly damage another person or organisation.</p>
          <p>You must not include the names, photographs, contact details, private messages, documents or unnecessarily identifying descriptions of individual landlords, agents, employees, tenants, family members or other persons.</p>
        </section>

        <section id="section-8">
          <h2>8. Photograph and File Rules</h2>
          <p>You may upload a photograph or file only if you created it or have the necessary rights and permission to submit it. You retain ownership of your content, subject to the licence granted in section 10.</p>
          <p>Uploads must not show identifiable people; passports, identity cards, contracts, mail or personal documents; names, phone numbers or email addresses; door codes, keys, access instructions or detailed security arrangements; illegal content; or material that infringes another person&rsquo;s privacy, copyright or other rights.</p>
          <p>Sparrow may blur, crop, resize, redact, remove metadata from, reject or remove uploads. These measures do not transfer the user&rsquo;s responsibility to check content before submission.</p>
        </section>

        <section id="section-9">
          <h2>9. Pseudonymous Display and Identification Risk</h2>
          <p>Sparrow normally displays a reviewer as &ldquo;A student from [verified university]&rdquo; or &ldquo;Verified student&rdquo; rather than displaying the user&rsquo;s name or email address.</p>
          <p>Sparrow does not promise complete anonymity. Someone familiar with the room, occupancy period, photographs or circumstances may infer who submitted a review. By submitting content, you acknowledge this residual risk.</p>
        </section>

        <section id="section-10">
          <h2>10. Licence to Sparrow</h2>
          <p>You retain any copyright or other rights you hold in content you submit. You grant Sparrow a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, format, crop, blur, redact, translate, analyse, display and otherwise use the content as reasonably necessary to operate, secure, moderate and improve the Platform; make it available to eligible users; and prepare filtered, paraphrased, room-specific or aggregated operator reports.</p>
          <p>This licence includes the right to use service providers for those purposes. Sparrow will not use identifiable room photographs in external advertising or promotional campaigns without separate permission. The licence ends when content is deleted from active systems, except for reasonable backup periods, legal records, moderation records and genuinely de-identified outputs.</p>
        </section>

        <section id="section-11">
          <h2>11. Operator Reports and User Consent</h2>
          <p>Sparrow may use information from your review to produce reports for the housing provider responsible for the reviewed accommodation and to benchmark that accommodation against comparable nearby rooms or properties.</p>
          <p>Reports may include specific room findings, paraphrased issues, frequencies, scores, trends and recommendations. Reports should identify the number of submissions and the limits of the available sample. Sparrow should not describe one subjective report as a universal fact or use absolute statements such as &ldquo;the worst&rdquo; without adequate supporting data and a clearly defined comparison set.</p>
          <p>Sparrow will not ordinarily disclose your name, email address or private account information to the housing provider. You acknowledge that the recipient may sometimes infer your identity from the room or circumstances.</p>
        </section>

        <section id="section-12">
          <h2>12. Prohibited Conduct</h2>
          <p>You must not:</p>
          <ul>
            <li>Use a student account if you are not the verified account holder, or obtain student access for a housing provider.</li>
            <li>Scrape, crawl, harvest, bulk-copy, reverse engineer or circumvent access controls, rate limits or security features.</li>
            <li>Publish personal data to expose, intimidate, threaten, harass or encourage harm to another person.</li>
            <li>Submit false, misleading, manipulated, fabricated or impersonated reviews or photographs.</li>
            <li>Upload malware or interfere with the Platform, its users, networks or service providers.</li>
            <li>Use the Platform for unlawful discrimination, threats, harassment, commercial spam or unauthorised advertising.</li>
            <li>Copy third-party listing photos, university photos, social-media images or other copyrighted content without permission.</li>
          </ul>
        </section>

        <section id="section-13">
          <h2>13. Moderation, Corrections and Removal</h2>
          <p>Sparrow may review, edit, redact, temporarily hide, reject or remove content and may request supporting information where necessary for privacy, safety, accuracy, legal compliance or enforcement of these Terms. Sparrow is not required to publish every submission.</p>
          <p>Students, housing providers and other affected persons may use Sparrow&rsquo;s reporting or correction channels. Objective property corrections and privacy complaints may be submitted without purchasing a subscription. Payment does not entitle a housing provider to remove honest negative reviews or obtain a reviewer&rsquo;s identity.</p>
          <p>Sparrow may preserve relevant records while investigating a complaint or complying with legal requirements.</p>
        </section>

        <section id="section-14">
          <h2>14. Suspension and Termination</h2>
          <p>Sparrow may limit, suspend or terminate access where it reasonably believes an account has been shared, misused, compromised or used in breach of these Terms, or where action is necessary to protect users, data, rights or the Platform.</p>
          <p>You may stop using the Platform and request account deletion. The treatment of reviews and retained records following deletion is described in the Privacy Policy.</p>
        </section>

        <section id="section-15">
          <h2>15. Intellectual Property</h2>
          <p>The Platform, Sparrow branding, software, designs, databases, reports, scoring structures and content created by Sparrow are owned by Sparrow or its licensors. Except for the limited right to use the Platform under these Terms, no rights are transferred to you.</p>
          <p>Third-party names, logos, maps and content remain the property of their respective owners. Their appearance on Sparrow does not imply affiliation or endorsement unless expressly stated.</p>
        </section>

        <section id="section-16">
          <h2>16. Third-Party Services and Links</h2>
          <p>The Platform may use or link to third-party mapping, authentication, hosting, analytics, communication or other services. Sparrow does not control third-party services and is not responsible for their separate terms, availability or privacy practices.</p>
        </section>

        <section id="section-17">
          <h2>17. Disclaimers</h2>
          <p>To the fullest extent permitted by law, the Platform and user-generated content are provided on an &ldquo;as available&rdquo; basis. Sparrow does not warrant that all reviews, photographs, locations, ratings or property details are complete, current, accurate or suitable for a particular decision.</p>
          <p>Sparrow does not guarantee that restricted content will never be accessed through credential sharing, screenshots, technical attacks or conduct outside Sparrow&rsquo;s reasonable control. This does not limit Sparrow&rsquo;s obligation to take reasonably practicable security measures or any liability that cannot lawfully be excluded.</p>
        </section>

        <section id="section-18">
          <h2>18. Limitation of Liability</h2>
          <p>Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited. Subject to that, Sparrow will not be liable for indirect, incidental or consequential loss, loss of opportunity, or loss arising solely from a user&rsquo;s breach of these Terms, unauthorised credential sharing, reliance on unverified user content or conduct of a third party outside Sparrow&rsquo;s reasonable control.</p>
          <p>Users remain responsible for independently evaluating accommodation and taking appropriate professional, legal, safety or financial advice where needed.</p>
        </section>

        <section id="section-19">
          <h2>19. Changes to the Platform or Terms</h2>
          <p>Sparrow may modify features, access rules or these Terms. The revised Terms will state their effective date. Where changes are material, Sparrow will provide reasonable notice through the Platform or other appropriate means. Continued use after the effective date constitutes acceptance of the revised Terms, except where additional consent is legally required.</p>
        </section>

        <section id="section-20">
          <h2>20. Governing Law and Jurisdiction</h2>
          <p>These Terms are governed by the laws of the Hong Kong Special Administrative Region. Subject to any rights that cannot lawfully be excluded, the courts of Hong Kong have jurisdiction over disputes arising from these Terms or the Platform.</p>
        </section>

        <section id="section-21">
          <h2>21. General</h2>
          <p>If any provision is held invalid or unenforceable, the remaining provisions continue in effect. Sparrow&rsquo;s failure to enforce a provision is not a waiver. You may not transfer your account or rights under these Terms without Sparrow&rsquo;s consent. Headings are for convenience only.</p>
        </section>

        <section id="section-22">
          <h2>22. Contact</h2>
          <p>Questions about these Terms may be sent to Sparrow. Please contact us through the Sparrow Platform.</p>
        </section>

        <section id="appendix-b" class="legal-appendix">
          <span class="legal-appendix-label">Appendix B</span>
          <h2>Review and Photo Submission Rules</h2>

          <h3>B.1 You may submit</h3>
          <ul>
            <li>Photos you took yourself, or photos you have clear permission to share.</li>
            <li>Bedrooms, bathrooms, kitchens, common areas and building areas directly relevant to the housing experience.</li>
            <li>Observable conditions such as water staining, mould, damage, cleanliness, pests or maintenance conditions.</li>
            <li>Honest first-hand descriptions of noise, ventilation, maintenance response, comfort, value and other housing experiences.</li>
          </ul>

          <h3>B.2 Do not submit</h3>
          <ul>
            <li>Faces or identifiable images of tenants, landlords, agents, staff, visitors or any other person.</li>
            <li>Names, phone numbers, email addresses, social-media handles, private messages or contact details.</li>
            <li>Passports, identity cards, student cards, tenancy agreements, receipts, mail, medical information or other personal documents.</li>
            <li>Keys, access cards, door codes, Wi-Fi passwords, security-camera details, entry instructions or information that could compromise safety.</li>
            <li>Images copied from a university, property listing, agency, social-media account or other source unless you have permission.</li>
            <li>Threats, harassment, discriminatory material, knowingly false claims or unsupported allegations about a person&rsquo;s intentions or character.</li>
          </ul>

          <h3>B.3 How to write a review</h3>
          <p>Describe what you personally observed and when relevant state that it was your experience. For example: &ldquo;There was a persistent bathroom odour during my stay&rdquo; is preferable to &ldquo;The landlord deliberately rents unsafe rooms.&rdquo;</p>
          <p>Do not name individuals. You may identify a verified university, incorporated operator or agency connected to the property, subject to Sparrow&rsquo;s moderation rules.</p>
        </section>

        <p class="legal-footer">
          See also Sparrow&rsquo;s <router-link to="/privacy-policy">Privacy Policy</router-link>.
          &middot;
          <router-link to="/">{{ $t('legal.backToApp_23fdde0') }}</router-link>
        </p>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsOfUse',
  data() {
    return {
      sections: [
        { id: 'section-1', label: '1. Agreement and Platform Operator' },
        { id: 'section-2', label: '2. Eligibility' },
        { id: 'section-3', label: '3. Student Accounts and Verification' },
        { id: 'section-4', label: '4. Restricted Review Access and Account Sharing' },
        { id: 'section-5', label: '5. Nature of the Service' },
        { id: 'section-6', label: '6. Property Pages, Maps and Verification Status' },
        { id: 'section-7', label: '7. Submitting Reviews' },
        { id: 'section-8', label: '8. Photograph and File Rules' },
        { id: 'section-9', label: '9. Pseudonymous Display and Identification Risk' },
        { id: 'section-10', label: '10. Licence to Sparrow' },
        { id: 'section-11', label: '11. Operator Reports and User Consent' },
        { id: 'section-12', label: '12. Prohibited Conduct' },
        { id: 'section-13', label: '13. Moderation, Corrections and Removal' },
        { id: 'section-14', label: '14. Suspension and Termination' },
        { id: 'section-15', label: '15. Intellectual Property' },
        { id: 'section-16', label: '16. Third-Party Services and Links' },
        { id: 'section-17', label: '17. Disclaimers' },
        { id: 'section-18', label: '18. Limitation of Liability' },
        { id: 'section-19', label: '19. Changes to the Platform or Terms' },
        { id: 'section-20', label: '20. Governing Law and Jurisdiction' },
        { id: 'section-21', label: '21. General' },
        { id: 'section-22', label: '22. Contact' },
        { id: 'appendix-b', label: 'Appendix B · Review and Photo Submission Rules' },
      ],
    };
  },
  computed: {
    // This app uses vue-router in hash mode (URLs look like /#/terms-of-use).
    // Anchor hrefs are built as "#<route-path>#<section-id>" so that a full
    // page load, copy-pasted link or middle-click still resolves to this
    // route (vue-router parses the "#section-id" suffix as route.hash),
    // instead of clobbering the router's own hash and landing nowhere.
    pageHashBase() {
      return '#' + (this.$route ? this.$route.path : '/terms-of-use');
    },
  },
  methods: {
    scrollTo(id, evt) {
      if (evt) evt.preventDefault();
      const el = document.getElementById(id);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Use replaceState (not router.push/location.hash) so the URL reflects
      // the section without firing a hashchange/popstate event: the app's
      // global router.scrollBehavior() always resets scroll to the top on
      // every navigation, which would otherwise undo the smooth scroll above.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', this.pageHashBase + '#' + id);
      }
      const details = this.$el.querySelector('.legal-toc-mobile');
      if (details) details.open = false;
    },
  },
  mounted() {
    // Support opening the page with a section already in the URL
    // (e.g. shared link built from this page's own TOC). Deferred with a
    // short delay because the app's global router.scrollBehavior() forces
    // the initial navigation to scroll to the top; this needs to run after
    // that resolves, not before.
    const hash = this.$route && this.$route.hash;
    if (hash) {
      const id = hash.replace(/^#/, '');
      this.$nextTick(() => {
        setTimeout(() => this.scrollTo(id), 150);
      });
    }
  },
};
</script>

<style>
@import './css/legal.css';
</style>
